import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  TextField,
} from "@mui/material";
import { styled } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  gaps,
} from "theme/defaultStyle";
import ActionButton from "controls/global/action-button/ActionButton";
import AddNoteIcon from "theme/icons/AddNoteIcon";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";

export { default as EditFieldsModal } from "./EditFieldsModal";
export { default as UploadRegistersModal } from "./UploadRegistersModal";
export { default as ConfirmCloseModal } from "./ConfirmCloseModal";
export { default as AdjustmentModal } from "./AdjustmentModal";
export { default as OverrideFinancialsModal } from "./OverrideFinancialsModal";
export { default as TransferModal } from "./TransferModal";
export { default as BulkAdjustModal } from "./BulkAdjustModal";

const DialogHeading = styled(DialogTitle)({
  fontSize: fontSize.xlarge,
  fontWeight: fontWeight.bold2,
  color: colors.blue15,
});

const ModalBody = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.medium1,
  minWidth: 480,
});

const PlaceholderText = styled(Typography)({
  fontSize: fontSize.small,
  color: colors.grey11,
});

// Small uppercase field label, matching the mockup's form-field captions.
const FieldLabel = styled(Typography)({
  fontSize: fontSize.small,
  fontWeight: fontWeight.bold1,
  color: colors.grey11,
  marginBottom: gaps.xsmall2,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
});

// Light-blue rounded badge that holds the title icon in the dialog header.
const HeaderIconBox = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 36,
  height: 36,
  borderRadius: borderRadius.small,
  backgroundColor: colors.blue08,
});

// Shared dialog header: icon badge + title + top-right close button.
const ModalHeader = ({
  icon,
  title,
  onClose,
}: {
  icon: React.ReactNode;
  title: string;
  onClose: () => void;
}) => (
  <DialogHeading
    sx={{ display: "flex", alignItems: "center", gap: gaps.small1 }}
  >
    <HeaderIconBox>{icon}</HeaderIconBox>
    <span>{title}</span>
    <IconButton
      aria-label="Close dialog"
      onClick={onClose}
      size="small"
      sx={{ marginLeft: "auto", color: colors.grey11 }}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  </DialogHeading>
);

interface BaseProps {
  open: boolean;
  onClose: () => void;
}

interface RecordsProps extends BaseProps {
  records: PolicyManagementRecord[];
  onSubmit?: (reason?: string) => void;
}

interface SingleRecordProps extends BaseProps {
  record: PolicyManagementRecord | null;
  onSubmit?: (reason?: string) => void;
}

const ModalShell = ({
  title,
  description,
  open,
  onClose,
  onSubmit,
  submitLabel = "Submit",
  cancelLabel = "Cancel",
}: BaseProps & {
  title: string;
  description: string;
  onSubmit?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="md">
    <DialogHeading>{title}</DialogHeading>
    <DialogContent>
      <ModalBody>
        <PlaceholderText>{description}</PlaceholderText>
      </ModalBody>
    </DialogContent>
    <DialogActions>
      <ActionButton variant="outlined" color="secondary" onClick={onClose}>
        {cancelLabel}
      </ActionButton>
      {onSubmit && (
        <ActionButton
          variant="outlined"
          color="primary"
          onClick={() => {
            onSubmit();
            onClose();
          }}
        >
          {submitLabel}
        </ActionButton>
      )}
    </DialogActions>
  </Dialog>
);

export const BulkEditModal = ({ open, onClose, records, onSubmit }: RecordsProps) => (
  <ModalShell
    title="Bulk Edit"
    description={`Apply a field patch to ${records.length} selected record${records.length === 1 ? "" : "s"}.`}
    open={open}
    onClose={onClose}
    onSubmit={onSubmit ? () => onSubmit() : undefined}
    submitLabel="Apply"
  />
);

export const PushBackModal = ({ open, onClose, records, onSubmit }: RecordsProps) => (
  <ModalShell
    title="Return to Quality Review"
    description={`Push ${records.length} record${records.length === 1 ? "" : "s"} back to Quality Review. Enter a reason.`}
    open={open}
    onClose={onClose}
    onSubmit={onSubmit ? () => onSubmit() : undefined}
    submitLabel="Return"
  />
);

export const ApplyHoldModal = ({ open, onClose, record, onSubmit }: SingleRecordProps) => (
  <ModalShell
    title="Apply Hold"
    description={`Place ${record?.PolicyNumber || "record"} on hold. Choose a reason.`}
    open={open}
    onClose={onClose}
    onSubmit={onSubmit ? () => onSubmit() : undefined}
    submitLabel="Apply Hold"
  />
);

export const RemoveHoldModal = ({ open, onClose, record, onSubmit }: SingleRecordProps) => (
  <ModalShell
    title="Remove Hold"
    description={`Remove the hold from ${record?.PolicyNumber || "record"}.`}
    open={open}
    onClose={onClose}
    onSubmit={onSubmit ? () => onSubmit() : undefined}
    submitLabel="Remove"
  />
);

interface ForcePromoteModalProps extends BaseProps {
  record?: PolicyManagementRecord | null; // single (drawer)
  count?: number; // bulk selection count
  sourceLabel?: string; // bulk subtitle source ("Quality Review" / "Pre-Close Queue")
  destinationLabel?: string; // banner destination; defaults to "Pre-Close Queue"
  // False for a record with no open errors: the move is an ordinary promotion,
  // not an override, so the dialog drops the "Force" wording and the
  // open-errors warning. Defaults to true — the override case.
  isForcePromote?: boolean;
  onSubmit?: (reason: string) => void;
}

export const ForcePromoteModal = ({
  open,
  onClose,
  record,
  count,
  sourceLabel,
  destinationLabel = "Pre-Close Queue",
  isForcePromote = true,
  onSubmit,
}: ForcePromoteModalProps) => {
  const [reason, setReason] = useState("");
  const maxChars = 1000;

  // Reset the reason whenever the dialog (re)opens.
  React.useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const isBulk = typeof count === "number";
  const effectiveCount = isBulk ? count ?? 0 : 1;
  const action = isForcePromote ? "Force Promote" : "Promote";
  const title = isBulk ? `Bulk ${action}` : action;
  const subtitle = isBulk
    ? `Selected from ${sourceLabel ?? ""}`
    : `Policy #: ${record?.PolicyNumber ?? "---"}`;
  const recordLabel = `${effectiveCount} record${effectiveCount !== 1 ? "s" : ""}`;
  const isValid = reason.trim().length > 0;

  const handleClose = () => {
    setReason("");
    onClose();
  };

  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit?.(reason);
    setReason("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogHeading
        sx={{ display: "flex", alignItems: "flex-start", gap: gaps.small1 }}
      >
        <HeaderIconBox sx={{ backgroundColor: "#FFF4E5" }}>
          <WarningAmberIcon sx={{ color: colors.yellow }} />
        </HeaderIconBox>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <span>{title}</span>
          <Typography
            sx={{
              fontSize: fontSize.small,
              fontWeight: fontWeight.normal2,
              color: colors.grey11,
              marginTop: "2px",
            }}
          >
            {subtitle}
          </Typography>
        </Box>
        <IconButton
          aria-label="Close dialog"
          onClick={handleClose}
          size="small"
          sx={{ marginLeft: "auto", color: colors.grey11 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogHeading>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: gaps.medium1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: gaps.small1,
              padding: gaps.small1,
              borderRadius: borderRadius.small,
              backgroundColor: "#FFF4E5",
              border: `1px solid ${colors.yellow}`,
            }}
          >
            <WarningAmberIcon sx={{ color: colors.yellow, fontSize: fontSize.large }} />
            <Typography sx={{ fontSize: fontSize.small, color: colors.grey08 }}>
              <strong>{recordLabel}</strong> will be moved to {destinationLabel}
              {isForcePromote
                ? " despite open errors. TVE errors will remain on these records."
                : "."}{" "}
              The reason you enter will be permanently attached to each
              record&apos;s audit trail.
            </Typography>
          </Box>
          <Box>
            <FieldLabel>
              Reason for {isForcePromote ? "Force Promoting" : "Promoting"}{" "}
              <span style={{ color: colors.red01 }}>*</span>
            </FieldLabel>
            <TextField
              fullWidth
              multiline
              minRows={4}
              maxRows={12}
              placeholder="Explain the business reason (e.g. compliance deadline, expedited closing)"
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, maxChars))}
              inputProps={{ maxLength: maxChars }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <ActionButton variant="outlined" color="secondary" onClick={handleClose}>
          Cancel
        </ActionButton>
        <ActionButton
          variant="contained"
          color="primary"
          startIcon={<WarningAmberIcon />}
          disabled={!isValid}
          onClick={handleSubmit}
        >
          {action} ({effectiveCount})
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
};

export const RemoveFPModal = ({ open, onClose, record, onSubmit }: SingleRecordProps) => (
  <ModalShell
    title="Remove Force Promote"
    description={`Clear force-promote on ${record?.PolicyNumber || "record"}.`}
    open={open}
    onClose={onClose}
    onSubmit={onSubmit ? () => onSubmit() : undefined}
    submitLabel="Remove"
  />
);

export const VerificationErrorsModal = ({ open, onClose, record }: SingleRecordProps) => {
  const errors = record?.errors || [];
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogHeading>Verification Errors</DialogHeading>
      <DialogContent>
        <ModalBody>
          {errors.length === 0 && (
            <PlaceholderText>No verification errors recorded.</PlaceholderText>
          )}
          {errors.map((e, i) => (
            <Box key={i} sx={{ borderLeft: `3px solid ${colors.red01}`, padding: gaps.small1 }}>
              <Typography sx={{ fontWeight: fontWeight.bold1, color: colors.red01 }}>
                {e.ErrorFieldName}
              </Typography>
              <Typography sx={{ fontSize: fontSize.small, color: colors.grey08 }}>
                {e.policyErrorStatusTypeCode}
              </Typography>
            </Box>
          ))}
        </ModalBody>
      </DialogContent>
      <DialogActions>
        <ActionButton variant="outlined" color="primary" onClick={onClose}>
          Close
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
};


const ReadOnlyField = styled(TextField)({
  "& .MuiInputBase-root": {
    backgroundColor: colors.grey25,
  },
});

export const CloseConfirmModal = ({ open, onClose, records, onSubmit }: RecordsProps) => (
  <ModalShell
    title="Close Selected Policies"
    description={`Close ${records.length} record${records.length === 1 ? "" : "s"}. This action is final and updates accounting.`}
    open={open}
    onClose={onClose}
    onSubmit={onSubmit ? () => onSubmit() : undefined}
    submitLabel="Confirm Close"
  />
);

export const AddNoteModal = ({ open, onClose, onSubmit }: SingleRecordProps) => {
  const [note, setNote] = useState("");
  const maxChars = 1000;
  const charCount = note.length;
  const isValid = note.trim().length > 0 && charCount <= maxChars;

  const handleClose = () => {
    setNote("");
    onClose();
  };

  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit?.(note);
    setNote("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <ModalHeader
        icon={<AddNoteIcon sx={{ color: colors.blue01 }} />}
        title="Add Note"
        onClose={handleClose}
      />
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: gaps.medium1 }}>
          <Box>
            <FieldLabel>
              Notes <span style={{ color: colors.red01 }}>*</span>
            </FieldLabel>
            <TextField
              fullWidth
              multiline
              minRows={6}
              maxRows={15}
              placeholder="Type your note here"
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, maxChars))}
              inputProps={{ maxLength: maxChars }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                fontSize: fontSize.xsmall,
                color: colors.grey11,
                marginTop: "4px",
              }}
            >
              {charCount}/{maxChars.toLocaleString()} characters
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <ActionButton variant="outlined" color="secondary" onClick={handleClose}>
          Cancel
        </ActionButton>
        <ActionButton
          variant="contained"
          color="primary"
          disabled={!isValid}
          onClick={handleSubmit}
        >
          Add
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
};

interface ViewNoteProps extends BaseProps {
  note?: string;
}

export const ViewNoteModal = ({ open, onClose, note }: ViewNoteProps) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <ModalHeader
      icon={<AddNoteIcon sx={{ color: colors.blue01 }} />}
      title="View Note"
      onClose={onClose}
    />
    <DialogContent>
      <Box sx={{ display: "flex", flexDirection: "column", gap: gaps.medium1 }}>
        <Box>
          <FieldLabel>Notes</FieldLabel>
          <ReadOnlyField
            fullWidth
            multiline
            minRows={6}
            maxRows={15}
            value={note && note.trim().length > 0 ? note : "No note attached"}
            InputProps={{ readOnly: true }}
          />
        </Box>
      </Box>
    </DialogContent>
    <DialogActions>
      <ActionButton variant="outlined" color="primary" onClick={onClose}>
        Done
      </ActionButton>
    </DialogActions>
  </Dialog>
);

interface EditNoteProps extends BaseProps {
  note?: string;
  onSubmit?: (text: string) => void;
}

export const EditNoteModal = ({ open, onClose, note, onSubmit }: EditNoteProps) => {
  const maxChars = 1000;
  const [value, setValue] = useState("");

  // Re-seed the field whenever a different note is opened for editing.
  React.useEffect(() => {
    if (open) setValue(note ?? "");
  }, [open, note]);

  const charCount = value.length;
  const isValid = value.trim().length > 0 && charCount <= maxChars;

  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit?.(value);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <ModalHeader
        icon={<EditNoteOutlinedIcon sx={{ color: colors.blue01 }} />}
        title="Edit Note"
        onClose={onClose}
      />
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: gaps.medium1 }}>
          <Box>
            <FieldLabel>
              Notes <span style={{ color: colors.red01 }}>*</span>
            </FieldLabel>
            <TextField
              fullWidth
              multiline
              minRows={6}
              maxRows={15}
              placeholder="Type your note here"
              value={value}
              onChange={(e) => setValue(e.target.value.slice(0, maxChars))}
              inputProps={{ maxLength: maxChars }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                fontSize: fontSize.xsmall,
                color: colors.grey11,
                marginTop: "4px",
              }}
            >
              {charCount}/{maxChars.toLocaleString()} characters
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <ActionButton variant="outlined" color="secondary" onClick={onClose}>
          Cancel
        </ActionButton>
        <ActionButton
          variant="contained"
          color="primary"
          disabled={!isValid}
          onClick={handleSubmit}
        >
          Save
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
};

export const ColumnChooserModal = ({ open, onClose }: BaseProps & { onApply?: (keys: string[]) => void }) => (
  <ModalShell
    title="Choose Columns"
    description="Toggle which columns are visible in this table."
    open={open}
    onClose={onClose}
    submitLabel="Apply"
  />
);

export const ExportModal = ({
  open,
  onClose,
  scope,
}: BaseProps & { scope: "qr" | "pcq" | "cq" | "history" }) => (
  <ModalShell
    title="Export"
    description={`Export the current ${scope.toUpperCase()} dataset to CSV or Excel.`}
    open={open}
    onClose={onClose}
    onSubmit={() => undefined}
    submitLabel="Export"
  />
);

export const ReportsHubModal = ({ open, onClose }: BaseProps) => (
  <ModalShell
    title="Reports Hub"
    description="Choose a report to generate."
    open={open}
    onClose={onClose}
  />
);

export const UnlockModal = ({ open, onClose, record, onSubmit }: SingleRecordProps) => (
  <ModalShell
    title="Unlock Record"
    description={`Release the edit lock on ${record?.PolicyNumber || "record"}.`}
    open={open}
    onClose={onClose}
    onSubmit={onSubmit ? () => onSubmit() : undefined}
    submitLabel="Unlock"
  />
);
